/**
 * Hook exposing discrete export actions: download and submission-upload.
 *
 * Consumers receive a built ExcelJS.Workbook (e.g. from ExportRunner's
 * onComplete callback) and decide which actions to perform.
 */

import { useCallback } from "react";
import { logger } from "@platform/browser-logger";
import { useApi } from "@platform/api";

import type ExcelJS from "exceljs";
import type { AxiosInstance } from "axios";

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const buildFilename = (rfpName: string): string => `${rfpName}-export.xlsx`;

const toBlob = async (workbook: ExcelJS.Workbook): Promise<Blob> => {
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: XLSX_MIME });
};

const triggerBrowserDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 500);
};

const postSubmissionExportFile = async (
  axiosInstance: AxiosInstance,
  rfpId: number,
  blob: Blob,
  filename: string,
): Promise<void> => {
  const formData = new FormData();
  formData.append("file", blob, filename);

  await axiosInstance.post(
    `/brokerportal/v1/rfp/${rfpId}/submission-export`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
};

export interface ExportActionsParams {
  rfpName: string;
}

export interface ExportActions {
  downloadExport: (workbook: ExcelJS.Workbook) => Promise<void>;
  uploadSubmissionExport: (
    workbook: ExcelJS.Workbook,
    rfpId: number,
  ) => Promise<void>;
}

export const useExportActions = ({
  rfpName,
}: ExportActionsParams): ExportActions => {
  const axiosInstance = useApi();

  const downloadExport = useCallback(
    async (workbook: ExcelJS.Workbook): Promise<void> => {
      const filename = buildFilename(rfpName);
      const blob = await toBlob(workbook);
      triggerBrowserDownload(blob, filename);
      logger.info("[btx export] Downloaded workbook", { filename });
    },
    [rfpName],
  );

  const uploadSubmissionExport = useCallback(
    async (workbook: ExcelJS.Workbook, rfpId: number): Promise<void> => {
      const filename = buildFilename(rfpName);
      const blob = await toBlob(workbook);
      try {
        await postSubmissionExportFile(axiosInstance, rfpId, blob, filename);
        logger.info("[btx export] Uploaded to SharePoint (submission-export)", {
          filename,
          rfpId,
        });
      } catch (error) {
        logger.error(
          "[btx export] Failed to upload to SharePoint (submission-export)",
          error as Error,
        );
        throw error;
      }
    },
    [axiosInstance, rfpName],
  );

  return { downloadExport, uploadSubmissionExport };
};
